import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Eye, EyeOff, TrendingUp, Users, FileText, CircleCheck as CheckCircle, Mail, Loader as Loader2 } from 'lucide-react';

const FEATURES = [
  { icon: TrendingUp, text: 'Gestão de contratos e empréstimos' },
  { icon: Users, text: 'Controle de clientes e cobranças' },
  { icon: FileText, text: 'Relatórios financeiros detalhados' },
  { icon: CheckCircle, text: 'Acompanhamento em tempo real' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { user, login } = useAuth();

  useEffect(() => {
    if (user) {
      const path = user.role === 'seller' ? '/dashboard/loan-simulation' :
                   user.role === 'manager' ? '/dashboard/contracts' :
                   user.role === 'admin' ? '/dashboard/units' :
                   '/dashboard/all-clients';
      navigate(path, { replace: true });
    }
  }, [user, navigate]);

  const validateForm = () => {
    if (!email.trim()) {
      setError('Por favor, insira seu e-mail.');
      return false;
    }
    if (!password.trim()) {
      setError('Por favor, insira sua senha.');
      return false;
    }
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await login(email.trim(), password.trim());
    } catch (err: any) {
      const errorMessage = err?.message || '';

      if (errorMessage.includes('Invalid login credentials') || errorMessage.includes('E-mail ou senha incorretos')) {
        setError('E-mail ou senha incorretos. Verifique suas credenciais e tente novamente.');
      } else if (errorMessage.includes('E-mail não confirmado')) {
        setError('E-mail não confirmado. Verifique sua caixa de entrada.');
      } else if (errorMessage.includes('Usuário não encontrado')) {
        setError('Usuário não encontrado no sistema.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
        setError('Erro de conexão com o servidor. Verifique sua internet e tente novamente.');
      } else {
        setError(errorMessage || 'Ocorreu um erro ao fazer login. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4 sm:p-6">

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-800/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-5xl">
        <div className="grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl shadow-black/40">

          {/* Left panel — institutional */}
          <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-600 to-blue-800 p-10 text-white relative overflow-hidden">
            {/* Subtle pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-8 right-8 w-64 h-64 border border-white/30 rounded-full" />
              <div className="absolute top-16 right-16 w-48 h-48 border border-white/20 rounded-full" />
              <div className="absolute bottom-8 left-8 w-48 h-48 border border-white/20 rounded-full" />
            </div>

            <div className="relative">
              {/* Logo */}
              <div className="mb-8 inline-block bg-white rounded-2xl p-3 shadow-lg shadow-black/20">
                <img
                  src="/ChatGPT_Image_18_de_jun._de_2026,_22_34_48.png"
                  alt="Ruta Controller"
                  className="h-28 w-auto object-contain"
                />
              </div>

              <h1 className="text-3xl font-bold leading-tight mb-4">
                Sistema de<br />Empréstimos
              </h1>
              <p className="text-blue-100 text-base leading-relaxed max-w-xs">
                Gerencie contratos, clientes e cobranças com eficiência e segurança em uma única plataforma.
              </p>
            </div>

            <div className="relative space-y-4">
              {FEATURES.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-blue-100 text-sm">{text}</span>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="h-px bg-white/20 mb-5" />
              <p className="text-blue-200 text-xs leading-relaxed">
                Acesso seguro e controlado por perfil de usuário.<br />
                Seus dados protegidos com criptografia de ponta a ponta.
              </p>
            </div>
          </div>

          {/* Right panel — login form */}
          <div className="bg-white flex flex-col justify-center p-6 sm:p-8 lg:p-10">

            {/* Mobile header */}
            <div className="lg:hidden mb-8 text-center">
              <div className="flex justify-center mb-3">
                <img
                  src="/ChatGPT_Image_18_de_jun._de_2026,_22_34_48.png"
                  alt="Ruta Controller"
                  className="h-24 w-auto object-contain"
                />
              </div>
              <p className="text-gray-500 text-sm mt-1">Acesse sua conta para continuar</p>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:block mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Bem-vindo de volta</h2>
              <p className="text-gray-500 text-sm">Acesse sua conta para gerenciar contratos, clientes e cobranças</p>
            </div>

            {/* Error alert */}
            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl" role="alert">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-0.5">Erro de autenticação</p>
                    <p className="text-sm text-red-600">{error}</p>
                    {error.includes('E-mail ou senha incorretos') && (
                      <p className="mt-2 text-xs text-red-500">
                        Senha padrão: <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono">defaultMASTER</code>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>

              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white
                      transition-all duration-200"
                    placeholder="seu@email.com"
                    required
                    autoComplete="email"
                    aria-label="Endereço de e-mail"
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white
                      transition-all duration-200"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    aria-label="Senha de acesso"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`
                  w-full py-3.5 rounded-xl font-semibold text-sm text-white
                  flex items-center justify-center gap-2
                  transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                  ${isLoading
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98] shadow-lg shadow-blue-200 hover:shadow-blue-300'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            {/* Footer */}
            <p className="mt-6 text-center text-xs text-gray-400">
              Acesso restrito a usuários autorizados
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
